import { Icon, tokens } from '@gripp/shared-ui';
import { useTranslation } from 'react-i18next';
import {
  Linking,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { buildOneLinkUrl } from './oneLinkUtils';

export type DropVariant = 'routine' | 'report';

type AnonymousDropProps = {
  variant: DropVariant;
  anonymousIssueIds?: string[];
};

const BarnWrenchMark = ({ size = 56 }: { size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 57 57" fill="none">
    <Path
      d="M41.8915 56V39.0636L46.3843 32.6283L43.8365 20.1676L38.9739 24.7866L36.751 13.1691L32.5459 9.84229L10.771 31.7852L18.9679 47.4618L20.9129 56H41.8915Z"
      fill={tokens.colors.orange[500]}
    />
    <Path
      d="M54.9018 7.41654L50.4601 11.6617C50.1185 11.9963 49.6303 12.1072 49.1786 11.9832L46.738 11.2774C46.262 11.1411 45.9083 10.7577 45.7981 10.2746L45.2242 7.72487C45.1141 7.25493 45.2606 6.75967 45.6143 6.42512L50.0318 2.19216C50.9222 1.35098 50.2147 -0.159286 49.0069 0.0136657C47.1645 0.260865 45.0655 1.01643 43.3939 1.73357C41.2099 2.6621 39.5747 4.5803 38.9886 6.90729C38.7693 7.7738 38.6106 8.70145 38.5863 9.59329C38.5499 10.7935 38.0982 11.9447 37.2321 12.7737L36.7509 13.1692L38.9738 24.7867L43.8364 20.1677L44.2488 19.8158C45.0664 19.0113 46.1649 18.6156 47.2998 18.6156C48.0931 18.6156 48.9229 18.5169 49.704 18.3684C52.0346 17.9229 53.9992 16.3507 55.0127 14.1853C55.7939 12.5143 56.6479 10.4231 56.9774 8.56606C57.1967 7.35277 55.7697 6.56138 54.8905 7.40256L54.9026 7.4148L54.9018 7.41654Z"
      fill={tokens.colors.neutral[700]}
    />
    <Path
      d="M2.28197 45.2275L0 48.4952C3.22182 50.81 7.69993 50.0422 9.98191 46.7744L12.2639 43.5067C9.04206 41.1919 4.56394 41.9597 2.28197 45.2266V45.2275Z"
      fill={tokens.colors.neutral[700]}
    />
    <Path
      d="M15.7545 41.2673L13.6676 43.3837C10.8732 46.2182 10.8732 50.8224 13.6676 53.656L16.462 50.8216C17.6454 49.6214 18.2263 47.9311 18.398 46.3711L15.7545 41.2664V41.2673Z"
      fill={tokens.colors.neutral[700]}
    />
    <Path
      d="M12.4456 34.9776C11.5795 35.3366 10.7463 35.7694 10.0501 36.4875L7.25573 39.3219C9.48916 41.5869 12.784 42.0748 15.4691 40.7384L12.4456 34.9776Z"
      fill={tokens.colors.neutral[700]}
    />
  </Svg>
);

// Keeps the OneLink URL within safe length limits
const MAX_ANONYMOUS_ISSUE_IDS = 30;

const getDownloadUrl = (anonymousIssueIds?: string[]) => {
  const params: Record<string, string> = {};
  if (anonymousIssueIds?.length) {
    params.anonymousIssueIds = anonymousIssueIds
      .slice(0, MAX_ANONYMOUS_ISSUE_IDS)
      .join(',');
  }
  return buildOneLinkUrl(params);
};

const HORIZON_BANDS: { color: string; height: number }[] = [
  { color: tokens.colors.orange[200], height: 6 },
  { color: tokens.colors.orange[300], height: 16 },
  { color: tokens.colors.orange[400], height: 24 },
  { color: tokens.colors.orange[500], height: 32 },
  { color: tokens.colors.orange[600], height: 40 },
  { color: tokens.colors.orange[700], height: 56 },
  { color: tokens.colors.orange[800], height: 72 },
];

export const AnonymousDrop = (props: AnonymousDropProps) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const titleKey =
    props.variant === 'routine'
      ? 'issue.anonymous.dropRoutineTitle'
      : 'issue.anonymous.dropReportTitle';
  const subtitleKey =
    props.variant === 'routine'
      ? 'issue.anonymous.dropRoutineSubtitle'
      : 'issue.anonymous.dropReportSubtitle';

  const onGetApp = async () => {
    try {
      await Linking.openURL(getDownloadUrl(props.anonymousIssueIds));
    } catch {
      // openURL can reject on blocked popups or unsupported URL schemes
    }
  };

  return (
    <View style={styles.page}>
      <View
        style={[
          styles.contentWrap,
          isMobile ? styles.contentWrapMobile : styles.contentWrapDesktop,
        ]}
      >
        <View style={styles.brandIcon}>
          <BarnWrenchMark size={56} />
        </View>
        <Text style={styles.title}>{t(titleKey)}</Text>
        <Text style={styles.subtitle}>{t(subtitleKey)}</Text>
        <Pressable
          style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}
          onPress={onGetApp}
          accessibilityRole="button"
          accessibilityLabel={t('issue.anonymous.dropCtaA11yLabel')}
        >
          <Icon name="Mobile" size={20} color={tokens.colors.brand.white} />
          <Text style={styles.ctaText}>{t('issue.anonymous.dropCta')}</Text>
        </Pressable>
      </View>

      <View style={styles.horizon} pointerEvents="none">
        {HORIZON_BANDS.map((band) => (
          <View
            key={band.color}
            style={[
              styles.horizonBand,
              { backgroundColor: band.color, height: band.height },
            ]}
          />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    width: '100%',
    backgroundColor: tokens.colors.orange[100],
    justifyContent: 'space-between',
  },
  contentWrap: {
    paddingTop: tokens.spacing['6xl'],
    paddingHorizontal: tokens.spacing['2xl'],
    alignItems: 'flex-start',
    alignSelf: 'center',
    width: '100%',
  },
  contentWrapMobile: {
    maxWidth: 480,
  },
  contentWrapDesktop: {
    maxWidth: 560,
    paddingTop: tokens.spacing['8xl'],
    paddingHorizontal: tokens.spacing['4xl'],
  },
  brandIcon: {
    marginBottom: tokens.spacing['2xl'],
  },
  title: {
    ...tokens.typography.headline.largeEmphasised,
    color: tokens.colors.neutral[800],
    marginBottom: tokens.spacing.lg,
  },
  subtitle: {
    ...tokens.typography.body.medium,
    color: tokens.colors.neutral[700],
    marginBottom: tokens.spacing['4xl'],
    maxWidth: 420,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    backgroundColor: tokens.colors.neutral[800],
    paddingHorizontal: tokens.spacing.xl,
    paddingVertical: tokens.spacing.md,
    borderRadius: tokens.borderRadius.md,
  },
  ctaPressed: {
    opacity: 0.85,
  },
  ctaText: {
    ...tokens.typography.body.mediumEmphasised,
    color: tokens.colors.brand.white,
  },
  horizon: {
    width: '100%',
    flexDirection: 'column',
  },
  horizonBand: {
    width: '100%',
  },
});
